<template>
	<div class="map-sticky bg-light border-bottom mx-n2 p-2 mx-md-0 px-md-0 z-1">
		<div class="map-controls d-flex gap-1 gap-md-4">
			<div class="form-floating flex-grow-1">
				<input
					id="floatingInput"
					ref="locationInput"
					type="search"
					class="form-control"
					placeholder="Search"
					data-testid="find-location-search-input"
				/>
				<label for="floatingInput">Search by Address, City or Zip</label>
			</div>
		</div>

		<LocationSearchPills v-if="showFilterPills" />

		<div class="d-flex justify-content-between mt-3">
			<div class="caption">
				<span v-if="zeroResults">No location found for search text "{{ locationString }}".</span>
				<span v-else class="mb-1">
					<span class="font-weight-bold">{{ matchingLocations.length }}</span>
					{{ resultText }}
				</span>
			</div>
			<a class="d-none d-lg-inline" :href="viewAllClubsUrl">View All Locations</a>
		</div>
		<div class="d-flex d-lg-none justify-content-between mt-2">
			<a :href="viewAllClubsUrl">View All Locations</a>
			<a v-if="showComingSoon" :href="comingSoonClubsUrl">Coming Soon Clubs</a>
		</div>
	</div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, ref, onBeforeMount } from 'vue';

import { useMapsLoader } from '@/composables/use-maps-loader';
import LocationSearchPills from './LocationSearchPills.vue';

import { useMapStore } from '@/stores/map';
import { useD360MapFiltersStore } from '@/stores/map-d360-filters';
import { getGlobalPageDriverUrl } from '@/util/globalPageDriver';
import { logError, logWarn } from '@/util/logger';

import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const props = defineProps<{
	matchingLocations: Location[];
}>();

const emit = defineEmits<{
	(e: 'set-search-position', latLng: google.maps.LatLngLiteral): void;
}>();

const mapStore = useMapStore();
const { showComingSoon, showFilterPills } = storeToRefs(mapStore);

const mapFiltersStore = useD360MapFiltersStore();
const { filtersCount, hasFilters } = storeToRefs(mapFiltersStore);

const comingSoonClubsUrl = getGlobalPageDriverUrl('comingSoonClubs');
const viewAllClubsUrl = getGlobalPageDriverUrl('viewAllClubs');

const locationString = ref('your location'); // the string they submitted to search
const zeroResults = ref(false); // true if their most-recent search text returned no results
const isSearching = ref(false); // whether we are doing HTTP for search
const locationInput = ref<HTMLInputElement | null>(null);

let geocoder: google.maps.Geocoder | null = null;
let autoComplete: google.maps.places.Autocomplete | null = null;

const { importLibrary } = useMapsLoader();

onBeforeMount(async () => {
	try {
		const { Geocoder } = await importLibrary('geocoding');
		geocoder = new Geocoder();
		await initAutoComplete();
	} catch (error) {
		logError('Error initializing location search', {}, error);
	}
});

const resultText = computed(() => {
	let text = props.matchingLocations.length === 1 ? 'result' : 'results';

	if (hasFilters.value) {
		text += ` matching your ${filtersCount.value === 1 ? 'filter' : 'filters'}`;
	}
	text += ` near ${locationString.value}.`;

	return text;
});

const onSearch = async (searchString: string) => {
	try {
		isSearching.value = true;
		zeroResults.value = false;

		// Add their search to the URL so if they go to a club details page
		// then click the browser BACK button, they'll be back to their search.
		const url = new URL(window.location.href);
		url.searchParams.set('query', searchString);
		window.history.replaceState(null, '', url);

		const response = await geocoder?.geocode({ address: searchString });
		const firstResult = response?.results?.[0];
		if (typeof firstResult?.geometry?.location?.lat === 'function') {
			const lat = firstResult.geometry.location.lat();
			const lng = firstResult.geometry.location.lng();
			emit('set-search-position', { lat, lng });
			locationString.value = firstResult.formatted_address;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error?.code === google.maps.GeocoderStatus.ZERO_RESULTS) {
			zeroResults.value = true;
			locationString.value = searchString;
		} else {
			logWarn('Error searching for location', { searchString }, error);
		}
	} finally {
		isSearching.value = false;
	}
};

const initAutoComplete = async () => {
	const options = {
		types: ['geocode']
	};
	const input = locationInput.value as HTMLInputElement;

	// If there is a URL query parameter named `query`
	// make it so search starts there to enable deep links like `?query=boston`
	const url = new URL(window.location.href);
	const query = url.searchParams.get('query');
	if (query) {
		input.value = query;
		onSearch(input.value);
	}

	const { Autocomplete } = await importLibrary('places');
	autoComplete = new Autocomplete(input, options);

	autoComplete?.addListener('place_changed', () => {
		onSearch(input.value);
	});
};
</script>
